import { Icon, IconProps, useTheme } from "@chakra-ui/react";
import React from "react";

type EmptyAIIconProps = IconProps & {
  colorScheme?: "purple";
};

/** "Empty AI Summary" icon in [Figma](https://www.figma.com/design/uN2Pi6GfUePpsJUUZORtBU/Prism?node-id=2387-202&m=dev) */
export const EmptyAIIcon: React.FC<EmptyAIIconProps> = ({
  colorScheme,
  ...props
}) => {
  const { colors } = useTheme();
  const palette =
    colorScheme === "purple"
      ? {
          100: colors.purple[100],
          200: colors.purple[200],
          300: colors.purple[300],
          400: colors.purple[400],
          500: colors.gray[500],
          board: "#EFE2FF",
        }
      : {
          100: colors.gray[100],
          200: colors.gray[200],
          300: colors.gray[300],
          400: colors.gray[300], // 400 being mapped to 300 here is intentional
          500: colors.gray[500],
          board: "#E8EAED",
        };

  return (
    <Icon width="241" height="179" viewBox="0 0 241 179" fill="none" {...props}>
      <g stroke={palette[100]} strokeLinecap="round">
        <path d="M48.8586 97V98.9859" />
        <path d="M44.3979 98.0762L46.0207 99.7467" />
        <path d="M42.5002 102.523H44.7947" />
        <path d="M44.2123 107.049L45.8351 105.379" />
        <path d="M48.8586 107.591V105.605" />
        <path d="M52.1679 106.268L50.8439 104.944" />
        <path d="M54.8161 102.958H52.8303" />
        <path d="M53.4921 98.3242L52.1682 99.6481" />
        <g strokeWidth="1.5">
          <path d="M116.934 2V3.32392" />
          <path d="M113 6H114.324" />
          <path d="M116.934 9.94355V8.61963" />
          <path d="M120.244 5.97178H118.92" />
          <path d="M40.1467 57.605V59.5909" />
          <path d="M34.1887 63.5625H36.1745" />
          <path d="M40.1467 68.858V66.8721" />
          <path d="M45.4421 63.5625H43.4562" />
          <path d="M183.458 85V86.9859" />
          <path d="M177.5 90.9575H179.486" />
          <path d="M183.458 96.253V94.2671" />
          <path d="M188.754 90.9575H186.768" />
          <path d="M182.636 129.784C184.218 129.784 185.501 131.067 185.501 132.65C185.501 134.233 184.218 135.516 182.636 135.516C181.054 135.516 179.771 134.233 179.771 132.65" />
        </g>
      </g>
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M21.1301 118.901C21.9809 118.636 23.2841 117.596 23.5859 116.477C23.8596 117.466 25.0066 118.636 26.0418 118.725C24.8768 119.137 23.7629 120.363 23.5859 121.326C23.4656 120.344 21.9247 119.044 21.1301 118.901Z"
        fill={palette[200]}
      />
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M36.9192 36.8173C36.9192 38.045 35.9245 39.0393 34.6977 39.0393C33.4704 39.0393 32.4762 38.045 32.4762 36.8173C32.4762 35.5902 33.4704 34.5952 34.6977 34.5952C35.9245 34.5952 36.9192 35.5902 36.9192 36.8173Z"
        fill={palette[100]}
      />
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M220.39 78.946C220.39 80.5734 219.072 81.8921 217.446 81.8921C215.819 81.8921 214.5 80.5734 214.5 78.946C214.5 77.3187 215.819 75.9999 217.446 75.9999C219.072 75.9999 220.39 77.3187 220.39 78.946Z"
        fill={palette[200]}
      />
      <g
        fillRule="evenodd"
        clipRule="evenodd"
        stroke={palette[100]}
        strokeWidth="1.5"
        strokeLinecap="round"
      >
        <path d="M50.2132 133.857C50.2132 134.883 49.382 135.714 48.3564 135.714C47.3313 135.714 46.5002 134.883 46.5002 133.857C46.5002 132.831 47.3313 132 48.3564 132C49.382 132 50.2132 132.831 50.2132 133.857Z" />
        <path d="M77.3322 22.5418C77.3322 23.7695 76.3375 24.7639 75.1107 24.7639C73.8839 24.7639 72.8892 23.7695 72.8892 22.5418C72.8892 21.3147 73.8839 20.3198 75.1107 20.3198C76.3375 20.3198 77.3322 21.3147 77.3322 22.5418Z" />
        <path d="M209.729 159.322C208.309 159.863 206.721 159.149 206.181 157.73C205.64 156.31 206.353 154.72 207.773 154.181C209.192 153.64 210.781 154.353 211.321 155.773C211.862 157.193 211.148 158.782 209.729 159.322Z" />
      </g>
      <rect
        x="88.0422"
        y="33.8989"
        width="90"
        height="106"
        rx="4"
        transform="rotate(11.2059 88.0422 33.8989)"
        fill={palette[200]}
      />
      <rect
        x="85.8767"
        y="29.3922"
        width="90"
        height="106"
        rx="4"
        transform="rotate(11.2059 85.8767 29.3922)"
        fill={palette.board}
      />
      <g opacity="0.8" clipPath="url(#clip0_3724_4508)">
        <path
          fillRule="evenodd"
          clipRule="evenodd"
          d="M129.472 115.833C129.459 115.575 129.273 115.359 129.019 115.309C128.766 115.258 128.511 115.387 128.401 115.621L128.06 116.345C127.75 117.003 127.102 117.436 126.376 117.472L125.576 117.511C125.318 117.524 125.102 117.711 125.052 117.964C125.002 118.217 125.13 118.472 125.364 118.582L126.088 118.924C126.746 119.234 127.179 119.881 127.215 120.608L127.255 121.407C127.267 121.665 127.454 121.881 127.707 121.931C127.961 121.982 128.215 121.853 128.326 121.62L128.667 120.895C128.977 120.237 129.625 119.804 130.351 119.768L131.151 119.729C131.409 119.716 131.625 119.529 131.675 119.276C131.725 119.023 131.596 118.768 131.363 118.658L130.639 118.316C129.981 118.006 129.547 117.359 129.512 116.632L129.472 115.833ZM122.41 116.71C122.39 116.459 122.206 116.253 121.959 116.204C121.713 116.155 121.464 116.276 121.349 116.499L120.336 118.475C119.892 119.34 119.034 119.915 118.065 119.995L115.852 120.178C115.602 120.199 115.395 120.383 115.347 120.63C115.298 120.876 115.418 121.125 115.642 121.24L117.618 122.253C118.482 122.697 119.057 123.555 119.137 124.524L119.321 126.737C119.342 126.987 119.526 127.194 119.772 127.242C120.019 127.291 120.268 127.171 120.382 126.947L121.396 124.971C121.839 124.106 122.698 123.532 123.666 123.451L125.879 123.268C126.13 123.247 126.336 123.063 126.385 122.817C126.434 122.57 126.313 122.321 126.09 122.207L124.114 121.193C123.249 120.75 122.674 119.891 122.594 118.923L122.41 116.71ZM126.396 125.505C126.366 125.265 126.185 125.071 125.947 125.024C125.71 124.977 125.469 125.087 125.349 125.298L124.887 126.111C124.711 126.419 124.401 126.626 124.05 126.671L123.122 126.788C122.882 126.819 122.688 127 122.641 127.237C122.594 127.475 122.704 127.716 122.915 127.835L123.728 128.298C124.036 128.473 124.243 128.783 124.288 129.134L124.405 130.062C124.436 130.303 124.616 130.496 124.854 130.543C125.092 130.59 125.333 130.48 125.452 130.27L125.915 129.457C126.09 129.149 126.4 128.942 126.751 128.897L127.679 128.779C127.919 128.749 128.113 128.568 128.16 128.331C128.207 128.093 128.097 127.852 127.887 127.732L127.074 127.27C126.766 127.094 126.559 126.785 126.514 126.433L126.396 125.505Z"
          fill={palette[500]}
        />
      </g>
      <g stroke={palette[300]}>
        <path
          opacity="0.6"
          d="M92.4561 55.4383L160.141 68.8474"
          strokeWidth="3"
          strokeLinecap="round"
        />
        <path
          opacity="0.6"
          d="M90.1241 67.2095L157.809 80.6187"
          strokeWidth="3"
          strokeLinecap="round"
        />
        <path
          opacity="0.6"
          d="M87.7921 78.9808L155.477 92.3899"
          strokeWidth="3"
          strokeLinecap="round"
        />
        <path
          opacity="0.6"
          d="M85.46 90.752L153.145 104.161"
          strokeWidth="3"
          strokeLinecap="round"
        />
        <path
          opacity="0.6"
          d="M83.128 102.523L150.813 115.932"
          strokeWidth="3"
          strokeLinecap="round"
        />
        <path
          opacity="0.6"
          d="M80.796 114.294L112.186 120.513"
          strokeWidth="3"
          strokeLinecap="round"
        />
        <path
          d="M147.24 51.5698L108.781 43.9507C108.413 43.8773 108.089 43.6604 107.88 43.3472L107.464 43.6249L107.88 43.3472C107.67 43.0341 107.594 42.6503 107.667 42.28L109.493 33.0635C109.566 32.6934 109.784 32.3679 110.096 32.1581C110.409 31.9484 110.791 31.8715 111.16 31.944L149.619 39.5632C149.987 39.6367 150.312 39.8536 150.521 40.1667C150.73 40.4799 150.807 40.8638 150.734 41.2342L148.908 50.4501C148.834 50.8203 148.617 51.146 148.304 51.3558C147.991 51.5656 147.609 51.6424 147.24 51.5698Z"
          fill={palette[400]}
        />
        <path
          d="M133.901 32.2387C133.603 33.7429 132.147 34.716 130.654 34.4202C129.16 34.1244 128.185 32.6695 128.483 31.1653C128.781 29.6611 130.237 28.6881 131.731 28.9839C133.224 29.2797 134.199 30.7345 133.901 32.2387Z"
          strokeWidth="3"
        />
      </g>
      <g fillRule="evenodd" clipRule="evenodd" fill={palette[100]}>
        <path d="M181.92 31.1672C182.649 31.4326 182.999 32.2067 182.703 32.8962L178.896 41.748C178.6 42.4375 177.769 42.7813 177.04 42.5159C176.312 42.2505 175.962 41.4764 176.258 40.7869L180.065 31.9351C180.361 31.2456 181.192 30.9018 181.92 31.1672Z" />
        <path d="M183.094 58.7427C183.415 57.9439 184.231 57.5216 184.916 57.7994L193.716 61.3661C194.401 61.6439 194.697 62.5166 194.376 63.3154C194.055 64.1142 193.239 64.5365 192.553 64.2587L183.754 60.6921C183.068 60.4142 182.773 59.5415 183.094 58.7427Z" />
        <path d="M163.307 30.7459C164.012 30.4283 164.796 30.733 165.058 31.4263L168.408 40.2939C168.67 40.9872 168.31 41.8066 167.605 42.1241C166.9 42.4416 166.116 42.137 165.854 41.4437L162.504 32.5761C162.242 31.8828 162.601 31.0634 163.307 30.7459Z" />
        <path d="M194.911 44.4275C195.173 45.1208 194.814 45.9402 194.109 46.2577L185.09 50.3188C184.385 50.6363 183.601 50.3317 183.339 49.6384C183.077 48.9451 183.436 48.1257 184.141 47.8082L193.16 43.7471C193.865 43.4295 194.649 43.7342 194.911 44.4275Z" />
      </g>
      <g
        fill={palette[200]}
        stroke={palette[200]}
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path d="M51.2228 80.4701L50.5 83L49.7772 80.4701C49.3975 79.1412 48.3588 78.1025 47.0299 77.7228L44.5 77L47.0299 76.2772C48.3588 75.8975 49.3975 74.8588 49.7772 73.5299L50.5 71L51.2228 73.5299C51.6025 74.8588 52.6412 75.8975 53.9701 76.2772L56.5 77L53.9701 77.7228C52.6412 78.1025 51.6025 79.1412 51.2228 80.4701Z" />
        <path d="M199.982 113.313L199.5 115L199.018 113.313C198.765 112.427 198.073 111.735 197.187 111.482L195.5 111L197.187 110.518C198.073 110.265 198.765 109.573 199.018 108.687L199.5 107L199.982 108.687C200.235 109.573 200.927 110.265 201.813 110.518L203.5 111L201.813 111.482C200.927 111.735 200.235 112.427 199.982 113.313Z" />
        <path d="M192.861 17.735L192.5 19L192.139 17.735C191.949 17.0706 191.429 16.5513 190.765 16.3614L189.5 16L190.765 15.6386C191.429 15.4487 191.949 14.9294 192.139 14.265L192.5 13L192.861 14.265C193.051 14.9294 193.571 15.4487 194.235 15.6386L195.5 16L194.235 16.3614C193.571 16.5513 193.051 17.0706 192.861 17.735Z" />
      </g>
      <ellipse
        opacity="0.8"
        cx="117"
        cy="167"
        rx="50.5"
        ry="3"
        fill={palette[100]}
      />
      <defs>
        <clipPath id="clip0_3724_4508">
          <rect
            width="18.0044"
            height="18.0044"
            fill="white"
            transform="translate(115.992 111.581) rotate(11.2059)"
          />
        </clipPath>
      </defs>
    </Icon>
  );
};
